import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Alert,
  Tooltip,
  Typography
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  EditOutlined,
  HistoryOutlined,
  DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { TextArea } = Input;
const { Text } = Typography;

// Create axios instance - use relative URL to go through Node.js proxy
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add auth headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const ProcessingTransactions = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [form] = Form.useForm();

  // Get current user
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = (user.role || '').toLowerCase().replace('_', '');
  const canAccess = userRole === 'superadmin' || userRole === 'finance';

  useEffect(() => {
    if (canAccess) {
      fetchProcessingTransactions();
    }
  }, [canAccess]);

  const fetchProcessingTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/transactions/processing');
      if (response.data.success) {
        setTransactions(response.data.transactions || []);
      } else {
        message.error('Gagal memuat data transaksi');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 403) {
        message.error('Akses ditolak. Hanya superadmin dan finance yang dapat mengakses halaman ini.');
      } else {
        message.error('Gagal memuat data transaksi: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const showUpdateModal = (record) => {
    setSelectedTransaction(record);
    form.resetFields();
    setUpdateModalVisible(true);
  };

  const handleUpdate = async (values) => {
    if (!selectedTransaction) return;

    setUpdating(true);
    try {
      const response = await api.post('/transactions/manual-update', {
        transaction_id: selectedTransaction.transaction_id,
        new_status: values.new_status,
        note: values.note
      });

      if (response.data.success) {
        message.success(`Transaksi berhasil diupdate ke ${values.new_status}`);
        setUpdateModalVisible(false);
        fetchProcessingTransactions(); // Refresh list

        // Show balance info if refunded
        if (values.new_status === 'FAILED' && response.data.balance_info) {
          Modal.info({
            title: 'Refund Berhasil',
            content: (
              <div>
                <p><strong>Saldo Dikembalikan:</strong> Rp {response.data.balance_info.refunded_amount?.toLocaleString('id-ID')}</p>
                <p><strong>Saldo User Sekarang:</strong> Rp {response.data.balance_info.saldo_after_refund?.toLocaleString('id-ID')}</p>
              </div>
            ),
          });
        }
      } else {
        message.error('Gagal mengupdate transaksi');
      }
    } catch (error) {
      console.error('Update error:', error);
      message.error('Gagal mengupdate: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 220,
      render: (text) => (
        <Tooltip title={text}>
          <Text copyable style={{ fontSize: '12px' }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.user_name || '-'}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>ID: {record.user_id}</div>
        </div>
      ),
    },
    {
      title: 'Produk',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 120,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{formatCurrency(record.amount)}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            Fee: {formatCurrency(record.fee_system)}
          </div>
          <div style={{ fontSize: '12px', color: '#1890ff', fontWeight: 500 }}>
            Total: {formatCurrency(record.total_amount)}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, record) => (
        <div>
          <Tag color="orange" icon={<SyncOutlined spin />}>
            {record.status_processing}
          </Tag>
          <br />
          <Tag color="blue" style={{ marginTop: 4 }}>
            {record.status_bayar}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Waktu',
      key: 'time',
      width: 160,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>Dibuat: {moment(record.created_at).format('DD/MM/YY HH:mm')}</div>
          <div>Update: {moment(record.updated_at).format('DD/MM/YY HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => showUpdateModal(record)}
          size="small"
        >
          Update
        </Button>
      ),
    },
  ];

  // Access denied view
  if (!canAccess) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Alert
          message="Akses Ditolak"
          description="Halaman ini hanya dapat diakses oleh Superadmin dan Finance."
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>
          <SyncOutlined style={{ marginRight: 8, color: '#faad14' }} />
          Transaksi Processing
        </h1>
        <p style={{ color: '#666' }}>
          Kelola transaksi dengan status PROCESSING yang memerlukan update manual
        </p>
      </div>

      {/* Info Alert */}
      <Alert
        message="Informasi"
        description={
          <div>
            <p>Transaksi dengan status <Tag color="orange">PROCESSING</Tag> adalah transaksi yang mengalami network error atau timeout saat hit ke supplier.</p>
            <p style={{ marginBottom: 0 }}>
              <strong>COMPLETED:</strong> Jika sudah dikonfirmasi berhasil dari dashboard supplier (saldo tetap terpotong).
              <br />
              <strong>FAILED:</strong> Jika dikonfirmasi gagal dari supplier (saldo akan di-refund otomatis).
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Processing"
              value={transactions.length}
              prefix={<SyncOutlined spin style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Nilai"
              value={transactions.reduce((sum, t) => sum + (parseFloat(t.total_amount) || 0), 0)}
              prefix={<DollarOutlined />}
              formatter={(val) => `Rp ${val.toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="User Terdampak"
              value={[...new Set(transactions.map(t => t.user_id))].length}
              prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Daftar Transaksi Processing
          </h3>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchProcessingTransactions}
            loading={loading}
          >
            Refresh
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="transaction_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} transaksi`,
          }}
          scroll={{ x: 1300 }}
          locale={{
            emptyText: 'Tidak ada transaksi dengan status PROCESSING'
          }}
        />
      </Card>

      {/* Update Modal */}
      <Modal
        title={
          <span>
            <EditOutlined style={{ marginRight: 8 }} />
            Update Manual Transaksi
          </span>
        }
        open={updateModalVisible}
        onCancel={() => setUpdateModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <div>
            {/* Transaction Info */}
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Transaction ID" span={2}>
                <Text copyable>{selectedTransaction.transaction_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedTransaction.user_name} (ID: {selectedTransaction.user_id})
              </Descriptions.Item>
              <Descriptions.Item label="Produk">
                {selectedTransaction.product_code}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {formatCurrency(selectedTransaction.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Fee">
                {formatCurrency(selectedTransaction.fee_system)}
              </Descriptions.Item>
              <Descriptions.Item label="Total" span={2}>
                <strong>{formatCurrency(selectedTransaction.total_amount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Status Saat Ini" span={2}>
                <Tag color="orange">{selectedTransaction.status_processing}</Tag>
                <Tag color="blue">{selectedTransaction.status_bayar}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Update Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdate}
            >
              <Form.Item
                name="new_status"
                label="Status Baru"
                rules={[{ required: true, message: 'Pilih status baru' }]}
              >
                <Select placeholder="Pilih status">
                  <Select.Option value="COMPLETED">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      COMPLETED - Transaksi berhasil (saldo tetap terpotong)
                    </Space>
                  </Select.Option>
                  <Select.Option value="FAILED">
                    <Space>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      FAILED - Transaksi gagal (saldo akan di-refund)
                    </Space>
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="note"
                label="Catatan / Alasan (Opsional)"
              >
                <TextArea
                  rows={3}
                  placeholder="Contoh: Dikonfirmasi berhasil dari dashboard LoketBayar, ref: LB123456"
                />
              </Form.Item>

              <Alert
                message="Perhatian"
                description={
                  <div>
                    <p style={{ marginBottom: 4 }}>Update ini akan dicatat sebagai:</p>
                    <p style={{ marginBottom: 0 }}>
                      <strong>Diupdate oleh:</strong> {user.full_name || user.username} ({user.role})
                      <br />
                      <strong>Waktu:</strong> {moment().format('DD/MM/YYYY HH:mm:ss')}
                    </p>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setUpdateModalVisible(false)}>
                    Batal
                  </Button>
                  <Button type="primary" htmlType="submit" loading={updating}>
                    Update Transaksi
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProcessingTransactions;
